import React from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import styled from 'styled-components';
import _ from 'lodash';
import { recordStatisticsEvent } from '../../../services/statistics-service';
import TruncatedEllipsis from '../../shared/truncated-ellipsis';
import EditorsManageWireCallout from '../../editors-manage-wire/editors-manage-wire-callout';
import theme from '../../../../assets/stylesheets/_settings.global.scss';

const Header = styled.div`
  & > h2 {
    margin-left: 0px;
    margin-right: 0px;
    margin-top: ${props => ((props.theme.isMobile || props.smallText) ? 0 : 5)}px;
    margin-bottom: ${props => (props.smallText ? 0 : 18)}px;

    > button {
      color: ${props => props.theme.bodyColor};
      text-align: left;
      font-family: ${props => props.theme.fontFamilyHeader};
      font-weight: ${props => props.theme.fontWeightBold};
      ${props => (props.theme.isMobile ? `
        font-size: 18px;
        line-height: 23px;
      ` : `
        font-size: 40px;
        line-height: 52px;
      `)};

      ${props => (props.smallText && `
        display: inline-block;
        line-height: 0px;
        > span {
          font-family: ${props.theme.fontFamilyBody};
          font-weight: ${props.theme.fontWeightHeavy};
          font-size: 14px;
          line-height: 17px;
          text-transform: uppercase;
          height: 32px;
          vertical-align: middle;
        }
      `)}

      ${props => (!props.smallText && `
        &:hover {
          text-decoration: none;
        }
      `)}
    }
  }

  & > .callout {
    display: inline;
    button {
      margin-top: 16px;
    }
  }
`;

export default function WireTitle({
  wire, showEditorManageTools, twoLinesTruncated, position,
}) {
  const isMobile = useMediaQuery({ query: `(max-width: ${theme.breakpointMediumMax})` });
  const showEditorManageWire = showEditorManageTools && !isMobile
    && (_.get(window, 'Livewire.currentUser.profile.canModifyWiresOfOthers') || wire.ownedByCurrentUser);

  const wireUrl = `/wires/${wire.slug}`;

  const handleClick = () => {
    recordStatisticsEvent({
      event: 'link_click',
      statisticable: 'wire',
      id: wire.id,
      clicked_url: wireUrl,
      page: window.location.pathname,
      custom_data: {
        page: 'search_results',
        position,
      },
    });
  };

  const wireClicked = e => {
    e && e.preventDefault();
    handleClick();
    window.location = wireUrl;
  };

  return (
    <Header smallText={twoLinesTruncated}>
      {
        showEditorManageWire && (
          <EditorsManageWireCallout wire={
            {
              wire_id: wire.id,
              title: wire.title,
              editors_picked: wire.editorsPicked,
              published_at: wire.publishedAt,
              featured_count: wire.featuredCount,
              wire: {
                slug: wire.slug,
                user_id: wire.userId || 0,
              },
              profile: {
                name: wire.profile.name,
                profile_picture_url: wire.profile.profilePictureUrl,
              },
            }
          }
          />
        )
      }
      <h2>
        <button type="button" onClick={wireClicked}>
          {
            !twoLinesTruncated
              ? wire.title
              : (
                <TruncatedEllipsis
                  lines={2}
                  summary={wire.title}
                  useReadMore={false}
                  hasTags={false}
                />
              )
          }
        </button>
      </h2>
    </Header>
  );
}

WireTitle.propTypes = {
  wire: PropTypes.shape({
    id: PropTypes.number.isRequired,
    userId: PropTypes.number,
    title: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    featuredCount: PropTypes.number.isRequired,
    profile: PropTypes.shape({
      name: PropTypes.string.isRequired,
      profilePictureUrl: PropTypes.string,
    }).isRequired,
    publishedAt: PropTypes.string,
    ownedByCurrentUser: PropTypes.bool,
    editorsPicked: PropTypes.bool,
  }).isRequired,
  showEditorManageTools: PropTypes.bool,
  twoLinesTruncated: PropTypes.bool,
  position: PropTypes.number.isRequired,
};

WireTitle.defaultProps = {
  showEditorManageTools: true,
  twoLinesTruncated: false,
};
